import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import '@chancenlogistik/client/components/logistik-component-error-message';
import '@chancenwerk/client/design/components/cr-card.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-loading-state.js';
import { LogistikOrderReminderAPI } from '@chancenwerk/db/api/LogistikOrderReminderAPI.js';
import { Weekday } from '@chancenwerk/db/enums/enums.js';
import { LogistikOrder } from '@chancenwerk/db/LogistikOrder.js';
import { LogistikOrderReminder } from '@chancenwerk/db/reminders.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { of, switchMap } from 'rxjs';

@customElement('logistik-dialog-order-reminder')
export class LogistikDialogOrderReminder extends ChromiumDialog<void> {
  static override get element_name() {
    return 'logistik-dialog-order-reminder';
  }
  override heading = 'Email-Benachrichtigungen';
  @property({ attribute: false }) orderID = new RXController<Maybe<string>>(this, undefined);

  @state() order = new RXController(this, this.orderID.observable, orderID$ =>
    orderID$.pipe(
      switchMap(orderID =>
        orderID ? getByID<typeof LogistikOrder>(LogistikOrder, orderID)({ _id: 1, processor: { email: 1 } }) : of(null),
      ),
    ),
  );

  @state() reminders = new RXController(this, this.orderID.observable, obs =>
    obs.pipe(
      switchMap(orderID =>
        orderID
          ? LogistikOrderReminderAPI.subscribeUnsentByOrderID(orderID)(selectionSet(() => LogistikOrderReminder, 1))
          : of([]),
      ),
    ),
  );

  override content() {
    if (this.order.isLoading || this.reminders.isLoading) return html` <cr-loading-state></cr-loading-state> `;
    if (!this.order.value)
      return html`
        <cr-empty-placeholder .text=${'Es wurde keine passende Bestellung gefunden.'} .img=${'close'}>
        </cr-empty-placeholder>
      `;
    return html`
      <cr-flex space="sm">
        ${this.emailInfoBox()}
        ${repeat(
          this.reminders.value!,
          reminder => reminder._id,
          reminder => this.reminderItem(reminder),
        )}
      </cr-flex>
    `;
  }

  private emailInfoBox(): Renderable {
    if (this.order.value?.processor?.email)
      return html`
        <logistik-component-error-message type="info">
          Benachrichtung geht an ${this.order.value.processor.email}
        </logistik-component-error-message>
      `;
    return html`
      <logistik-component-error-message type="warning">
        Es ist kein Bearbeiter mit gültiger Email angegeben
      </logistik-component-error-message>
    `;
  }

  protected override footerContent(): Renderable {
    return html` <cr-button icon="add" mode="action" @click=${() => this.onAddClicked()}> Hinzufügen </cr-button> `;
  }

  private async onAddClicked() {
    if (!this.orderID.value) return;
    const dt = await SelectDateDialog.showPopup<Maybe<Date>>();
    if (!dt) return;
    await new LogistikOrderReminder({
      orderID: this.orderID.value,
      dueDate: dt,
    }).create();
  }

  private reminderItem(reminder: LogistikOrderReminder): Renderable {
    return html`
      <cr-card>
        <cr-flex space="sm" horizontal space-between>
          <cr-flex space="sm">
            <cr-text> ${Object.values(Weekday).at(reminder.dueDate.getDay() - 1)} </cr-text>
            <cr-text>
              ${String(reminder.dueDate.getDate()).padStart(2, '0')}.${String(reminder.dueDate.getMonth()).padStart(
                2,
                '0',
              )}.${reminder.dueDate.getFullYear()}
            </cr-text>
          </cr-flex>
          <cr-flex horizontal space="sm">
            <cr-button mode="icon" icon="edit" @click=${() => this.onEditItem(reminder)}></cr-button>
            <cr-button mode="icon" icon="delete" @click=${() => this.onDeleteItem(reminder)}> </cr-button>
          </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }

  private async onEditItem(reminder: LogistikOrderReminder) {
    const dt = await SelectDateDialog.showPopup<Maybe<Date>>();
    if (!dt) return;
    reminder.dueDate = dt;
    await reminder.save();
  }

  private async onDeleteItem(reminder: LogistikOrderReminder) {
    const confirm = await ChromiumDialog.confirm('Benachrichtigung löschen?');
    if (confirm) await reminder.remove();
  }
}

@customElement('select-date-dialog')
class SelectDateDialog extends ChromiumDialog<Maybe<Date>> {
  static override get element_name() {
    return 'select-date-dialog';
  }
  override heading = 'Datum auswählen';
  @state() private selectedDate = new RXController<Maybe<DateTime>, Maybe<DateTime>>(
    this,
    DateTime.now().plus({ days: 1 }),
  );
  override content() {
    return html`
      <cr-basic-date-picker
        .value=${this.selectedDate.value}
        @value-picked=${(event: ValueEvent<DateTime>) => {
          this.selectedDate.next(event.detail.value);
        }}
        .isDateBlocked=${(date: DateTime) => date < DateTime.now().endOf('day')}
      >
      </cr-basic-date-picker>
    `;
  }

  protected override footerContent(): Renderable {
    return html`
      <cr-flex horizontal space="sm">
        <cr-button @click=${() => this.close(null)}> Abbrechen </cr-button>
        <cr-button @click=${() => this.close(this.selectedDate.value?.toJSDate())}> Speichern </cr-button>
      </cr-flex>
    `;
  }
}
